import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['checkbox', 'checkboxAll', 'group']

  initialize() {
    this.checkboxAllByGroup = new Map()
    this.connected = false
  }

  connect() {
    this.connected = true
    this.#refreshAllContainers()

    this.#addGroupEventListeners(this.element)
  }

  disconnect() {
    this.connected = false
  }

  checkboxConnected() {
    this.#refreshAllContainers()
  }

  checkboxDisconnected() {
    this.#refreshAllContainers()
  }

  checkboxAllTargetConnected(checkbox) {
    checkbox.addEventListener('change', this.#dispatchToggleAllEvent.bind(this))

    const group = checkbox.closest(`[data-${this.identifier}-target="group"]`) ||
      this.element

    const checkboxAllArray = this.checkboxAllByGroup.get(group) || []

    this.checkboxAllByGroup.set(group, [...checkboxAllArray, checkbox])

    this.#refreshAllContainers()
  }

  checkboxAllTargetDisconnected(checkbox) {
    checkbox.removeEventListener(
      'change',
      this.#dispatchToggleAllEvent.bind(this),
    )

    const group = checkbox.closest(`[data-${this.identifier}-target="group"]`) ||
      this.element

    const checkboxAllArray = this.checkboxAllByGroup.get(group) || []

    this.checkboxAllByGroup.set(
      group,
      checkboxAllArray.filter((checkboxAll) => checkboxAll !== checkbox),
    )

    this.#refreshAllContainers()
  }

  groupTargetConnected(group) {
    this.#addGroupEventListeners(group)

    this.#refreshAllContainers()
  }

  groupTargetDisconnected(group) {
    group.removeEventListener(
      `${this.identifier}:toggle-all`,
      this.#handleToggleAll.bind(this),
    )

    group.removeEventListener('change', this.#handleChange.bind(this))

    this.#refreshAllContainers()
  }

  _refreshCheckboxAll(checkboxAll, checkboxes, checked) {
    checkboxAll.checked = checkboxes.length > 0 && checked.length === checkboxes.length

    checkboxAll.indeterminate = checked.length > 0 &&
      checked.length < checkboxes.length
  }

  _shouldToggle(container, checkbox) {
    return container.contains(checkbox)
  }

  #addGroupEventListeners(el) {
    el.addEventListener(
      `${this.identifier}:toggle-all`,
      this.#handleToggleAll.bind(this),
    )

    el.addEventListener('change', this.#handleChange.bind(this))
  }

  #dispatchToggleAllEvent(event) {
    this.dispatch('toggle-all', { target: event.target })
  }

  #handleChange(event) {
    if (this.checkboxTargets.includes(event.target) ||
        this.checkboxAllTargets.includes(event.target)) {
      this.#refreshContainer(event.currentTarget)
    }
  }

  #handleToggleAll(event) {
    event.stopPropagation()

    this.#toggle(event.currentTarget, event.target.checked)
  }

  #refreshAllContainers() {
    if (this.connected) {
      [...this.groupTargets, this.element].forEach((container) => {
        this.#refreshContainer(container)
      })
    }
  }

  #refreshContainer(container) {
    const checkboxes = container.
      querySelectorAll(`[data-${this.identifier}-target="checkbox"]`)
    const checked = Array.from(checkboxes).filter((checkbox) => checkbox.checked)

    this.checkboxAllByGroup.get(container)?.forEach((checkboxAll) => {
      this._refreshCheckboxAll(checkboxAll, checkboxes, checked)
    })
  }

  #toggle(container, checked) {
    this.checkboxAllTargets.
      forEach((checkbox) => {
        if (this._shouldToggle(container, checkbox)) {
          checkbox.checked = checked
          checkbox.indeterminate = false
          this.#triggerInputEvent(checkbox)
        }
      })

    this.checkboxTargets.
      forEach((checkbox) => {
        if (this._shouldToggle(container, checkbox)) {
          checkbox.checked = checked
          checkbox.indeterminate = false
          this.#triggerInputEvent(checkbox)
        }
      })
  }

  #triggerInputEvent(checkbox) {
    const inputEvent = new Event('input', { bubbles: true, cancelable: true })

    checkbox.dispatchEvent(inputEvent)
  }
}
